import { z } from "zod";
import { shopAuthorSchema } from "./catalog-books.js";
import { publicImageSchema, shopCategorySchema, shopLocationSchema, shopProductCardSchema, } from "./storefront.js";
/**
 * Themes v2 section library (ST-10, PRD-storefront-themes §5.2): more fixed section types for the page builder. Like
 * B-80's, they hold plain text, IDs, numbers and uploaded files only — never HTML or scripts; links are shop paths,
 * `https:` or `tel:`; videos are YouTube / Facebook addresses reduced to an embed ID by the API. Each has an input
 * form (what the Admin saves) and a shop form (what the storefront renders: public image URLs, live product cards).
 */
const text = (max) => z.string().trim().max(max);
const link = z
    .string()
    .trim()
    .max(300)
    .regex(/^(\/[^\s]*|https:\/\/[^\s]+|tel:\+?\d+)?$/);
const image = z.uuid().nullable().default(null);
const percent = z.number().min(0).max(100);
export const FEATURE_ICONS = [
    "truck",
    "cash",
    "shield",
    "refresh",
    "gift",
    "phone",
    "star",
    "leaf",
    "clock",
    "badge",
];
/** Input forms (saved in `storefront_pages`). */
export const sectionsV2 = [
    z.object({
        type: z.literal("heroSlider"),
        slides: z
            .array(z.object({
            title: text(120),
            subtitle: text(300).default(""),
            buttonLabel: text(40).default(""),
            buttonHref: link.default(""),
            imageFileId: image,
        }))
            .min(1)
            .max(3),
        autoplay: z.boolean().default(true),
        /** ST-20: a muted background loop behind the first slide (an uploaded MP4); its picture is the poster. */
        videoFileId: z.uuid().nullable().default(null),
    }),
    z.object({
        type: z.literal("heroSplit"),
        title: text(120),
        subtitle: text(300).default(""),
        buttonLabel: text(40).default(""),
        buttonHref: link.default(""),
        imageFileId: image,
        imageSide: z.enum(["left", "right"]).default("right"),
        chips: z.array(text(30).min(1)).max(4).default([]),
    }),
    z.object({
        type: z.literal("categoryTiles"),
        title: text(120).default(""),
        style: z.enum(["grid", "circles", "large", "rail"]).default("grid"),
        /** Empty: the shop's top-level categories (with published products). */
        tiles: z
            .array(z.object({ categoryId: z.uuid(), imageFileId: image }))
            .max(12)
            .default([]),
    }),
    z.object({
        type: z.literal("flashDeal"),
        title: text(120),
        /** A real end time set by the merchant (PRD §8): the section disappears once it has passed. */
        endsAt: z.iso.datetime(),
        categoryId: z.uuid().nullable().default(null),
        productIds: z.array(z.uuid()).max(12).default([]),
        limit: z.int().min(2).max(12).default(8),
    }),
    z.object({
        type: z.literal("lookbook"),
        title: text(120).default(""),
        imageFileId: z.uuid(),
        spots: z
            .array(z.object({ x: percent, y: percent, productId: z.uuid() }))
            .max(6)
            .default([]),
    }),
    z.object({
        type: z.literal("beforeAfter"),
        title: text(120).default(""),
        body: text(500).default(""),
        beforeImageFileId: z.uuid(),
        afterImageFileId: z.uuid(),
    }),
    z.object({
        type: z.literal("featureIcons"),
        title: text(120).default(""),
        items: z
            .array(z.object({ icon: z.enum(FEATURE_ICONS), title: text(60).min(1), text: text(160).default("") }))
            .min(2)
            .max(6),
    }),
    z.object({
        type: z.literal("marquee"),
        items: z.array(text(80).min(1)).min(1).max(8),
        speed: z.enum(["slow", "normal", "fast"]).default("normal"),
    }),
    z.object({
        type: z.literal("logoWall"),
        title: text(120).default(""),
        imageFileIds: z.array(z.uuid()).min(1).max(12),
    }),
    z.object({
        type: z.literal("ugcGrid"),
        title: text(120).default(""),
        items: z
            .array(z.object({
            imageFileId: z.uuid(),
            caption: text(80).default(""),
            productId: z.uuid().nullable().default(null),
        }))
            .min(1)
            .max(12),
    }),
    z.object({
        type: z.literal("video"),
        title: text(120).default(""),
        /** A YouTube or Facebook video address; the API keeps only the provider and the video's ID. */
        url: z
            .string()
            .trim()
            .max(300)
            .regex(/^https:\/\/(www\.|m\.)?(youtube\.com|youtu\.be|facebook\.com|fb\.watch)\/[^\s]+$/),
        posterFileId: image,
    }),
    z.object({
        type: z.literal("comparisonTable"),
        title: text(120).default(""),
        ours: text(40).min(1),
        theirs: text(40).min(1),
        rows: z
            .array(z.object({ label: text(80).min(1), ours: z.boolean(), theirs: z.boolean() }))
            .min(1)
            .max(10),
    }),
    z.object({
        type: z.literal("stats"),
        title: text(120).default(""),
        /** Real numbers the merchant states (counted up when seen). */
        items: z
            .array(z.object({
            value: z.int().min(0).max(100_000_000),
            suffix: text(4).default(""),
            label: text(60).min(1),
        }))
            .min(2)
            .max(4),
    }),
    z.object({
        type: z.literal("steps"),
        title: text(120).default(""),
        items: z
            .array(z.object({ title: text(60).min(1), text: text(200).default("") }))
            .min(2)
            .max(5),
    }),
    z.object({
        type: z.literal("finder"),
        title: text(120),
        question: text(160).min(1),
        /** Each answer leads to a collection, search or page (skin type, age, budget…). */
        answers: z
            .array(z.object({ label: text(60).min(1), href: link.refine((v) => v !== "") }))
            .min(2)
            .max(6),
    }),
    z.object({
        type: z.literal("bundle"),
        title: text(120),
        productIds: z.array(z.uuid()).min(2).max(4),
    }),
    z.object({
        type: z.literal("deliveryInfo"),
        title: text(120).default(""),
        body: text(1000).default(""),
        /** Show the district checker (A6). */
        checker: z.boolean().default(true),
    }),
    z.object({
        type: z.literal("richBanner"),
        title: text(120),
        subtitle: text(300).default(""),
        buttonLabel: text(40).default(""),
        buttonHref: link.default(""),
        imageFileId: z.uuid(),
        align: z.enum(["left", "center", "right"]).default("left"),
    }),
    z.object({ type: z.literal("recentlyViewed"), title: text(120).default("") }),
    /** ST-11 (A8): the branches marked visible on the shop, with map links. */
    z.object({ type: z.literal("storeLocator"), title: text(120).default(""), body: text(500).default("") }),
    /** ST-11 (A7): a signed-in shopper's recent items with "Add all to cart"; nothing for anyone else. */
    z.object({ type: z.literal("orderAgain"), title: text(120).default("") }),
    /** DG-08 (BK-01): the authors with the most published books, round photos linking to their pages. */
    z.object({
        type: z.literal("authors"),
        title: text(120).default(""),
        limit: z.int().min(4).max(24).default(10),
    }),
];
/** Shop forms (what the storefront renders). */
export const shopSectionsV2 = [
    z.object({
        type: z.literal("heroSlider"),
        slides: z.array(z.object({
            title: z.string(),
            subtitle: z.string(),
            buttonLabel: z.string(),
            buttonHref: z.string(),
            image: publicImageSchema.nullable(),
        })),
        autoplay: z.boolean(),
        /** ST-20: the background loop's public address (null: none, or not public yet). */
        video: z.object({ url: z.string() }).nullable().default(null),
    }),
    z.object({
        type: z.literal("heroSplit"),
        title: z.string(),
        subtitle: z.string(),
        buttonLabel: z.string(),
        buttonHref: z.string(),
        image: publicImageSchema.nullable(),
        imageSide: z.enum(["left", "right"]),
        chips: z.array(z.string()),
    }),
    z.object({
        type: z.literal("categoryTiles"),
        title: z.string(),
        style: z.enum(["grid", "circles", "large", "rail"]),
        tiles: z.array(z.object({ category: shopCategorySchema, image: publicImageSchema.nullable() })),
    }),
    z.object({
        type: z.literal("flashDeal"),
        title: z.string(),
        endsAt: z.iso.datetime(),
        products: z.array(shopProductCardSchema),
    }),
    z.object({
        type: z.literal("lookbook"),
        title: z.string(),
        image: publicImageSchema.nullable(),
        spots: z.array(z.object({ x: z.number(), y: z.number(), product: shopProductCardSchema })),
    }),
    z.object({
        type: z.literal("beforeAfter"),
        title: z.string(),
        body: z.string(),
        before: publicImageSchema.nullable(),
        after: publicImageSchema.nullable(),
    }),
    z.object({
        type: z.literal("featureIcons"),
        title: z.string(),
        items: z.array(z.object({ icon: z.enum(FEATURE_ICONS), title: z.string(), text: z.string() })),
    }),
    z.object({
        type: z.literal("marquee"),
        items: z.array(z.string()),
        speed: z.enum(["slow", "normal", "fast"]),
    }),
    z.object({ type: z.literal("logoWall"), title: z.string(), images: z.array(publicImageSchema) }),
    z.object({
        type: z.literal("ugcGrid"),
        title: z.string(),
        items: z.array(z.object({ image: publicImageSchema, caption: z.string(), productSlug: z.string().nullable() })),
    }),
    z.object({
        type: z.literal("video"),
        title: z.string(),
        provider: z.enum(["youtube", "facebook"]),
        /** YouTube video ID, or the Facebook video address for its embed player. */
        videoId: z.string(),
        poster: publicImageSchema.nullable(),
    }),
    z.object({
        type: z.literal("comparisonTable"),
        title: z.string(),
        ours: z.string(),
        theirs: z.string(),
        rows: z.array(z.object({ label: z.string(), ours: z.boolean(), theirs: z.boolean() })),
    }),
    z.object({
        type: z.literal("stats"),
        title: z.string(),
        items: z.array(z.object({ value: z.int(), suffix: z.string(), label: z.string() })),
    }),
    z.object({
        type: z.literal("steps"),
        title: z.string(),
        items: z.array(z.object({ title: z.string(), text: z.string() })),
    }),
    z.object({
        type: z.literal("finder"),
        title: z.string(),
        question: z.string(),
        answers: z.array(z.object({ label: z.string(), href: z.string() })),
    }),
    z.object({
        type: z.literal("bundle"),
        title: z.string(),
        products: z.array(shopProductCardSchema),
        /** Sum of each in-stock product's lowest price (server, `packages/domain`); null when one is out of stock. */
        totalPaisa: z.int().nullable(),
    }),
    z.object({ type: z.literal("deliveryInfo"), title: z.string(), body: z.string(), checker: z.boolean() }),
    z.object({
        type: z.literal("richBanner"),
        title: z.string(),
        subtitle: z.string(),
        buttonLabel: z.string(),
        buttonHref: z.string(),
        image: publicImageSchema.nullable(),
        align: z.enum(["left", "center", "right"]),
    }),
    z.object({ type: z.literal("recentlyViewed"), title: z.string() }),
    z.object({
        type: z.literal("storeLocator"),
        title: z.string(),
        body: z.string(),
        locations: z.array(shopLocationSchema),
    }),
    z.object({ type: z.literal("orderAgain"), title: z.string() }),
    z.object({ type: z.literal("authors"), title: z.string(), authors: z.array(shopAuthorSchema) }),
];
//# sourceMappingURL=storefront-sections.js.map